import React, { useCallback, useMemo, useState } from 'react';
import { Avatar, Box, Collapse, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PushPinIcon from '@mui/icons-material/PushPin';
import RoomIcon from '@mui/icons-material/Room';
import StarIcon from '@mui/icons-material/Star';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { fetchRoster, RosterEntry } from '../api/characters';
import { fetchSystemIntel } from '../api/intel';
import { fetchMaps } from '../api/maps';
import { fetchFavoriteSystems, searchSystems, SystemSearchResult } from '../api/systems';
import { MapFolder, PinnedSystem } from '../types';
import { PresenceEvent, usePresenceStream } from '../hooks/usePresenceStream';
import { useSystemHistory } from '../hooks/useSystemHistory';
import { useDashboardPrefs } from '../hooks/useDashboardPrefs';
import Chevron from '../components/Chevron';
import ConsolePanel from '../components/ConsolePanel';
import PanelHeader from '../components/PanelHeader';
import ScanFrame from '../components/ScanFrame';
import StatusBadge from '../components/StatusBadge';
import { C, FONT, classColor, depthSx, frameSx, glow, hexToRgba, lcarsLabelSx, prefersReducedMotion, revealSx } from '../theme/tokens';

const ROSTER_POLL_MS = 45_000; // matches the backend roster cache TTL — no point polling faster than it refreshes

/** The post-login landing page — hero + system search, the linked-character
 * roster (online status, current system, ship), and a browser-local
 * last-10-visited-systems history. This page never renders intel itself:
 * selecting a system (search, roster click, or a pill) navigates to that
 * system's own page at `/system/<name>`, the canonical shareable URL.
 *
 * Its entry banner listens to the live presence stream. The roster, vault and
 * system-page refresh that goes with a move runs app-wide (usePresenceSync). */
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { history } = useSystemHistory();
  const [lastEntry, setLastEntry] = useState<PresenceEvent | null>(null);
  const roster = useQuery({ queryKey: ['characters', 'roster'], queryFn: fetchRoster, refetchInterval: ROSTER_POLL_MS });
  const favorites = useQuery({ queryKey: ['systems', 'favorites'], queryFn: fetchFavoriteSystems, refetchInterval: ROSTER_POLL_MS });
  // Shares the ['maps'] cache key with the Data Vault page — whichever loads
  // first pays for the fetch. Carries the per-vault rollups the panel below
  // reads (entries, auto-tracked, open connections).
  const vaults = useQuery({ queryKey: ['maps'], queryFn: fetchMaps, refetchInterval: ROSTER_POLL_MS });

  // A system's own name is the slug — no id->name resolve needed anywhere,
  // since every list that links to a system already carries the name.
  const openSystem = (systemName: string) => navigate(`/system/${encodeURIComponent(systemName)}`);

  // Only J-space entries show a banner. The roster and vault refresh for a
  // move happens app-wide in usePresenceSync, so it isn't repeated here.
  const onPresence = useCallback((event: PresenceEvent) => {
    setLastEntry((previous) =>
      event.systemClass ? event : previous?.eveCharacterId === event.eveCharacterId ? null : previous,
    );
  }, []);
  usePresenceStream(onPresence, true);

  const { prefs } = useDashboardPrefs();

  // A character sitting in a system right now isn't "recent" — the roster
  // row above already says where they are. Drop those out so the row is
  // purely where the character has been, most recent (excluding current)
  // leftmost, oldest last, still capped at the hook's own last-10 buffer.
  const currentSystemIds = useMemo(
    () => new Set((roster.data ?? []).map((r) => r.solarSystemId).filter((id): id is number => id != null)),
    [roster.data],
  );
  const recentHistory = useMemo(() => history.filter((h) => !currentSystemIds.has(h.solarSystemId)), [history, currentSystemIds]);

  // Anything already on screen above doesn't earn a second pill: a starred
  // system that's also a vault pin was showing up twice, identically, two
  // rows apart.
  const shownAbove = useMemo(
    () =>
      new Set([...recentHistory.map((h) => h.solarSystemId), ...(favorites.data?.pinned ?? []).map((p) => p.solar_system_id)]),
    [recentHistory, favorites.data],
  );
  const starred = (favorites.data?.personal ?? []).filter((s) => !shownAbove.has(s.solar_system_id));

  return (
    <Stack spacing={3} sx={revealSx()}>
      <Hero />

      {lastEntry && (
        <PresenceBanner event={lastEntry} onOpen={() => openSystem(lastEntry.systemName)} onDismiss={() => setLastEntry(null)} />
      )}

      {/* The live page: whichever system the active character is in, updated on
          every jump. The pills below are fixed snapshots of one named system. */}
      <SystemPillRow label="Live" accent={C.green}>
        <SystemPill label="Current system" accent={C.green} onClick={() => navigate('/intel/current')} />
      </SystemPillRow>

      {/* Quick-access rows sit ABOVE the roster by direct instruction: the
          system you were just in is what you reach for first on landing;
          the character list is reference, not the primary action. */}
      {recentHistory.length > 0 && (
        <SystemPillRow label="Recent Systems" accent={C.cyan}>
          {recentHistory.map((h) => (
            <SystemPill key={h.solarSystemId} label={h.name} accent={C.cyan} onClick={() => openSystem(h.name)} />
          ))}
        </SystemPillRow>
      )}

      {roster.data && roster.data.length > 0 && <RosterList roster={roster.data} onOpenSystem={openSystem} />}

      {/* Vault quick-access sits directly below the character boxes by direct
          instruction: after "who am I flying", the next question is "where do
          we live and what did we flag". */}
      {prefs.showVaultPanel && (vaults.data?.length ?? 0) > 0 && (
        <VaultQuickAccess vaults={vaults.data ?? []} entries={favorites.data?.pinned ?? []} onOpenSystem={openSystem} />
      )}

      {starred.length > 0 && (
        <SystemPillRow label="Starred" accent={C.wormhole}>
          {starred.map((s) => (
            <SystemPill
              key={s.solar_system_id}
              label={s.name}
              accent={s.system_class ? classColor(s.system_class) : C.blue}
              onClick={() => openSystem(s.name)}
              glyph="star"
            />
          ))}
        </SystemPillRow>
      )}
    </Stack>
  );
};

/** The one thing on this page that arrives unprompted: a character just
 * jumped into a J-space system. Colored by that system's danger class, so
 * an alt wandering into a C5 reads differently from one popping into a C1.
 * Stays until dismissed or replaced — a wormhole entry is worth a beat of
 * attention, and auto-hiding it would lose the one event you stepped away
 * from the screen for. */
const PresenceBanner: React.FC<{ event: PresenceEvent; onOpen: () => void; onDismiss: () => void }> = ({
  event,
  onOpen,
  onDismiss,
}) => {
  const accent = classColor(event.systemClass);
  const frameColor = accent.startsWith('#') ? accent : C.blue;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        ...frameSx(frameColor),
        border: `1px solid ${hexToRgba(frameColor, 0.45)}`,
        px: 2.5,
        py: 1.25,
        animation: prefersReducedMotion() ? 'none' : 'presenceArrive 0.45s cubic-bezier(0.16,1,0.3,1) both',
        '@keyframes presenceArrive': {
          from: { opacity: 0, transform: 'translateY(-6px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Chevron color={frameColor} size={10} pulse />
      <Typography sx={{ color: C.text, fontSize: '1rem' }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {event.characterName}
        </Box>{' '}
        entered{' '}
        <Box
          component="span"
          onClick={onOpen}
          sx={{ color: accent, fontFamily: FONT.mono, fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          {event.systemName}
        </Box>
      </Typography>
      <StatusBadge label={event.systemClass ?? ''} accent={accent} size="md" />
      <Box sx={{ flex: 1 }} />
      <Typography
        onClick={onDismiss}
        sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.78rem', cursor: 'pointer', '&:hover': { color: C.text } }}
      >
        dismiss
      </Typography>
    </Stack>
  );
};

/**
 * Vault quick-access: every home system and shared pin the user can see,
 * grouped by the vault it lives in. This is the "collection of systems" the
 * vault holds, surfaced where the day starts — one click from the dashboard
 * instead of two navigations into the vault page.
 *
 * Home systems lead each group and carry the wormhole accent; pins keep the
 * gold they have everywhere else in the app.
 */
const VaultQuickAccess: React.FC<{
  vaults: MapFolder[];
  entries: PinnedSystem[];
  onOpenSystem: (name: string) => void;
}> = ({ vaults, entries, onOpenSystem }) => {
  const rows = useMemo(() => {
    const pinsByVault = new Map<string, PinnedSystem[]>();
    for (const e of entries) {
      if (!pinsByVault.has(e.folder_name)) pinsByVault.set(e.folder_name, []);
      pinsByVault.get(e.folder_name)!.push(e);
    }
    // Every vault now gets a row, not only the ones that happen to hold a
    // pin — an empty staging vault existing at all is information, and its
    // absence used to read as "you have no vaults".
    return vaults
      .map((v) => ({ vault: v, pins: pinsByVault.get(v.name) ?? [] }))
      .sort((a, b) => {
        // The vault you live in goes first, then the busiest.
        const aHome = a.pins.some((s) => s.is_home_system);
        const bHome = b.pins.some((s) => s.is_home_system);
        if (aHome !== bHome) return aHome ? -1 : 1;
        return (b.vault.entry_count ?? 0) - (a.vault.entry_count ?? 0);
      });
  }, [vaults, entries]);

  return (
    <ConsolePanel
      title="Data Vault"
      accent={C.gold}
      action={
        <Box component={RouterLink} to="/vault" sx={{ textDecoration: 'none' }}>
          <StatusBadge label="open vault" accent={C.cyan} />
        </Box>
      }
    >
      <Stack spacing={1.75}>
        {rows.map(({ vault, pins }) => (
          <Box key={vault.id}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
              <Typography sx={lcarsLabelSx(C.textDim, '0.66rem')}>{vault.name}</Typography>
              <StatusBadge label={SEAL_LABEL[vault.share_scope] ?? vault.share_scope} accent={C.wormhole} dim />
              <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.66rem', color: C.textDim }}>
                {vault.entry_count ?? 0} {vault.entry_count === 1 ? 'entry' : 'entries'}
                {(vault.open_connection_count ?? 0) > 0 && ` · ${vault.open_connection_count} open`}
              </Typography>
              {/* Auto-tracked entries appear without anyone doing anything, so
                  the vault they land in has to say that it is doing it. */}
              {vault.auto_track === 1 && (
                <StatusBadge
                  label={(vault.auto_count ?? 0) > 0 ? `auto · ${vault.auto_count} live` : 'auto-track on'}
                  accent={C.gold}
                  dim
                />
              )}
            </Stack>
            {vault.description && (
              <Typography sx={{ color: C.textDim, fontSize: '0.76rem', mb: 0.75, maxWidth: 620 }}>
                {vault.description}
              </Typography>
            )}
            {pins.length > 0 ? (
              <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                {pins.map((s) => (
                  <SystemPill
                    key={`${vault.id}-${s.solar_system_id}`}
                    label={s.is_home_system ? `${s.name} · home` : s.tag ? `${s.name} · ${s.tag}` : s.name}
                    accent={s.is_home_system ? C.wormhole : s.system_class ? classColor(s.system_class) : C.gold}
                    onClick={() => onOpenSystem(s.name)}
                    glyph={s.is_home_system ? undefined : 'pin'}
                  />
                ))}
              </Stack>
            ) : (
              <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.textDim }}>
                {(vault.entry_count ?? 0) > 0 ? 'nothing pinned — open the vault to browse' : 'no systems saved yet'}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </ConsolePanel>
  );
};

const SEAL_LABEL: Record<string, string> = {
  private: 'SEALED',
  corp: 'CORP',
  alliance: 'ALLIANCE',
  group: 'GROUP',
};

/** A labeled row of clickable system pills — the one layout `Recent
 * Systems`, `Pinned`, and `Starred` all share, so the three read as one
 * family instead of three hand-tweaked lists. */
const SystemPillRow: React.FC<{ label: string; accent: string; children: React.ReactNode }> = ({ label, accent, children }) => (
  <Box>
    <PanelHeader label={label} accent={accent} dense sx={{ mb: 1.25 }} />
    <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
      {children}
    </Stack>
  </Box>
);

/** `glyph` swaps StatusBadge's default chevron for a small pin/star mark so
 * a pinned-from-vault system and a personally-starred one read differently
 * at a glance, not just by which row they sit in. */
const SystemPill: React.FC<{ label: string; accent: string; onClick: () => void; glyph?: 'pin' | 'star' }> = ({
  label,
  accent,
  onClick,
  glyph,
}) => (
  <Box onClick={onClick} sx={{ cursor: 'pointer', transition: 'transform 0.15s ease', '&:hover': { transform: 'translateY(-1px)' } }}>
    <StatusBadge
      label={label}
      accent={accent}
      size="md"
      icon={glyph === 'pin' ? <PushPinIcon sx={{ fontSize: 13 }} /> : glyph === 'star' ? <StarIcon sx={{ fontSize: 13 }} /> : undefined}
    />
  </Box>
);

/** The hero's one authored entrance, in this world's vocabulary: the
 * bracket frame locks on, the title fades up with its glow swelling, then
 * the search settles. v12's elbow-draws-then-rail-extends sequence went out
 * with the LCARS chrome it was announcing. */
const HERO_FRAME_MS = 0;
const HERO_TITLE_MS = 220;
const HERO_SEARCH_MS = 480;

/**
 * The masthead: the product's name at display scale in Orbitron, glowing in
 * wormhole purple, inside the corner-bracket frame this world uses to mean
 * "live readout" — then the search directly under it as the primary action.
 *
 * Centred, because the search is the one thing every visit starts with and
 * the brief asked for it back in the middle. v12 left-aligned it against an
 * LCARS rail, which put the page's primary control off-axis from everything
 * below it.
 */
const Hero: React.FC = () => {
  const reduced = prefersReducedMotion();
  const anim = (name: string, ms: number, dur = 0.55) =>
    reduced ? 'none' : `${name} ${dur}s cubic-bezier(0.16,1,0.3,1) ${ms}ms both`;

  return (
    <Stack alignItems="center" spacing={2.5} sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 1, md: 2 }, position: 'relative', zIndex: 20 }}>
      <ScanFrame
        color={C.cyan}
        sx={{
          display: 'inline-block',
          backgroundColor: 'transparent',
          backdropFilter: 'none',
          border: 'none',
          px: { xs: 3, md: 5 },
          py: { xs: 1.5, md: 2 },
          '&:hover': { boxShadow: 'none', borderColor: 'transparent' },
          animation: anim('heroFrameLock', HERO_FRAME_MS, 0.5),
          '@keyframes heroFrameLock': {
            '0%': { opacity: 0, transform: 'scale(1.04)' },
            '100%': { opacity: 1, transform: 'scale(1)' },
          },
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontFamily: FONT.display,
            fontWeight: 700,
            fontSize: 'clamp(1.6rem, 5vw, 3rem)',
            color: C.wormhole,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            lineHeight: 1,
            textAlign: 'center',
            m: 0,
            textShadow: glow(C.wormhole, 22, 0.45),
            animation: reduced
              ? 'none'
              : `heroTitleIn 0.7s cubic-bezier(0.16,1,0.3,1) ${HERO_TITLE_MS}ms both, heroTitleGlow 4.2s ease-in-out ${HERO_TITLE_MS + 700}ms infinite`,
            '@keyframes heroTitleIn': {
              '0%': { opacity: 0, transform: 'translateY(10px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
            '@keyframes heroTitleGlow': {
              '0%, 100%': { textShadow: glow(C.wormhole, 22, 0.45) },
              '50%': { textShadow: glow(C.wormhole, 36, 0.68) },
            },
          }}
        >
          Wormhole Gobbo
        </Typography>
        <Typography
          sx={{
            ...lcarsLabelSx(hexToRgba(C.cyan, 0.9), '0.78rem'),
            textAlign: 'center',
            mt: 1,
            animation: anim('heroTagIn', HERO_TITLE_MS + 180, 0.5),
            '@keyframes heroTagIn': { '0%': { opacity: 0 }, '100%': { opacity: 1 } },
          }}
        >
          J-Space Intel Console
        </Typography>
      </ScanFrame>

      <Box
        sx={{
          width: '100%',
          animation: anim('heroSearch', HERO_SEARCH_MS, 0.6),
          '@keyframes heroSearch': {
            '0%': { opacity: 0, transform: 'translateY(10px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <DashboardSystemSearch />
      </Box>
    </Stack>
  );
};

/**
 * System search.
 *
 * The bug this fixes: results rendered in normal document flow, so every
 * keystroke pushed the character roster down the page ("the searchbar when
 * searching for results is moving the characters below"). They now render in
 * an absolutely-positioned panel anchored under the input inside a
 * `position: relative` wrapper — nothing below it moves, and because the
 * panel is anchored *below* rather than over the field, it never repeats the
 * older Autocomplete-Popper overlap either.
 *
 * Keyboard works properly: up/down move the highlight, Enter opens the
 * highlighted system's overview, Escape closes. A search box you have to
 * reach for the mouse to finish is not a fast search box.
 *
 * Picking a result goes straight to that system's page. It used to expand a
 * second row of Overview/Sites/Killboard chips instead, which put a
 * deliberate choice in front of the one destination almost every search
 * wants — and the two sub-pages are one click away from the system page's
 * own tabs anyway. Search resolves; it does not negotiate.
 */
const DashboardSystemSearch: React.FC = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const [open, setOpen] = useState(false);

  const results = useQuery({
    queryKey: ['system-search', q],
    queryFn: () => searchSystems(q),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });

  const rows = (results.data ?? []).slice(0, 8);
  const showPanel = open && q.trim().length >= 2;
  const openSystem = (name: string) => navigate(`/system/${encodeURIComponent(name)}`);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!showPanel || rows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (c + 1) % rows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (c - 1 + rows.length) % rows.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      openSystem(rows[cursor].name);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 720, mx: 'auto', zIndex: 5 }}>
      <Box sx={{ ...frameSx(C.cyan), px: 2.5, py: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <TextField
            fullWidth
            value={q}
            autoComplete="off"
            onChange={(e) => {
              setQ(e.target.value);
              setCursor(0);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder="Search any system — J-space or known space…"
            sx={{
              '& .MuiOutlinedInput-root': { fontFamily: FONT.mono, fontSize: '1.05rem' },
              '& .MuiOutlinedInput-input': { py: 1 },
              '& fieldset': { border: 'none' },
            }}
          />
          <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.74rem'), display: { xs: 'none', sm: 'block' }, flexShrink: 0 }}>
            {rows.length > 0 ? `${rows.length} match${rows.length === 1 ? '' : 'es'}` : '↵ to open'}
          </Typography>
        </Stack>
      </Box>

      {showPanel && (
        <Box
          sx={{
            ...frameSx(C.wormhole),
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 20,
            backgroundColor: hexToRgba(C.panel, 0.97),
            backdropFilter: 'blur(18px) saturate(1.3)',
            ...depthSx(),
            p: 1,
            maxHeight: '52vh',
            overflowY: 'auto',
          }}
        >
          {results.isLoading ? (
            <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.9rem', px: 1.25, py: 1 }}>
              Scanning registry&hellip;
            </Typography>
          ) : rows.length === 0 ? (
            <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.9rem', px: 1.25, py: 1 }}>
              No system matches &ldquo;{q}&rdquo;.
            </Typography>
          ) : (
            <Stack spacing={0.5} role="listbox" aria-label="System search results">
              {rows.map((r, i) => (
                <SearchResultRow
                  key={r.solar_system_id}
                  result={r}
                  index={i}
                  highlighted={i === cursor}
                  onHover={() => setCursor(i)}
                  onOpen={() => openSystem(r.name)}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

/** One result, and one destination: clicking it opens that system. The class
 * colour owns the row's left rail, so the danger read happens before the name
 * is even parsed — and the rows stagger in so the list arrives as a sequence
 * rather than a slab. */
const SearchResultRow: React.FC<{
  result: SystemSearchResult;
  index: number;
  highlighted: boolean;
  onHover: () => void;
  onOpen: () => void;
}> = ({ result, index, highlighted, onHover, onOpen }) => {
  const accent = classColor(result.system_class);
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      role="option"
      aria-selected={highlighted}
      onMouseEnter={onHover}
      onClick={onOpen}
      sx={{
        position: 'relative',
        px: 1.5,
        py: 1.1,
        borderRadius: '3px',
        cursor: 'pointer',
        backgroundColor: highlighted ? hexToRgba(accent, 0.14) : 'transparent',
        boxShadow: highlighted ? `inset 3px 0 0 ${accent}` : `inset 3px 0 0 ${hexToRgba(accent, 0.3)}`,
        transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
        animation: prefersReducedMotion() ? 'none' : `resultIn 0.28s cubic-bezier(0.16,1,0.3,1) ${index * 28}ms both`,
        '@keyframes resultIn': {
          '0%': { opacity: 0, transform: 'translateX(-6px)' },
          '100%': { opacity: 1, transform: 'translateX(0)' },
        },
      }}
    >
      <Typography sx={{ color: accent, fontFamily: FONT.mono, fontWeight: 700, fontSize: '1.02rem' }}>{result.name}</Typography>
      {result.system_class && <StatusBadge label={result.system_class} accent={accent} size="md" />}
      <Box sx={{ flex: 1 }} />
      {/* Named on the highlighted row only: it confirms what the click does
          and that Enter does the same, without printing the word eight times
          down the list. Held in a fixed-width slot so the hint appearing
          never shifts the name beside it. */}
      <Typography
        sx={{
          ...lcarsLabelSx(C.text, '0.72rem'),
          flexShrink: 0,
          opacity: highlighted ? 1 : 0,
          transition: 'opacity 0.15s ease',
        }}
      >
        open &crarr;
      </Typography>
    </Stack>
  );
};

/** Green = flying, dim = logged off, gold = we cannot tell.
 *
 * The third colour is the point: an expired token or an ESI outage used to
 * render as the same dim dot as a pilot who simply logged off, so a character
 * that had silently stopped reporting days ago looked like someone taking the
 * evening off. Gold is the app's "needs your attention" colour everywhere
 * else, which is exactly what an expired link needs. */
const PRESENCE_DOT = (presence: RosterEntry['presence']) =>
  presence === 'online' ? C.green : presence === 'unknown' ? C.gold : C.textDim;

const PRESENCE_LABEL = (r: RosterEntry): string => {
  if (r.presence === 'online') return 'Online';
  if (r.presence === 'offline') return 'Offline';
  return r.presenceReason === 'auth_expired'
    ? 'Link expired — re-authenticate this character to resume tracking'
    : 'Status unavailable — ESI did not answer';
};

/** All linked characters (main pinned first, guaranteed by the backend's
 * own ordering), each showing online status, current system, and ship
 * type. A character currently in a J-space system gets an expand chevron
 * revealing a compact intel summary (matching DataVault's FolderSystemRow
 * pattern) without leaving the dashboard. */
const RosterList: React.FC<{ roster: RosterEntry[]; onOpenSystem: (systemName: string) => void }> = ({ roster, onOpenSystem }) => {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <ConsolePanel title="Characters" accent={C.wormhole}>
      <Stack spacing={1}>
        {roster.map((r) => {
          const isJSpace = !!r.systemClass;
          const isExpanded = expanded === r.eveCharacterId;
          // The row's accent reflects the character's actual current danger
          // class (classColor) when known — a character sitting in a C5
          // reads red, Thera reads purple — real signal, not a flat
          // main/alt split. Falls back to main/alt only with no location.
          const rowAccent = r.systemClass ? classColor(r.systemClass) : r.isMain ? C.blue : C.cyan;
          // The class badge repeats/overlaps the system name itself in
          // exotic space ("Thera" system, class "Thera"; "Azdaja Redoubt",
          // class "Redoubt") — showing both prints the same word twice.
          // Skip the badge when the name already carries it.
          const classIsRedundant = !!r.systemClass && !!r.systemName && r.systemName.toLowerCase().includes(r.systemClass.toLowerCase());
          return (
            <Box key={r.eveCharacterId}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                useFlexGap
                flexWrap="wrap"
                onClick={() => r.systemName && onOpenSystem(r.systemName)}
                sx={{
                  ...frameSx(rowAccent),
                  px: 2.5,
                  py: 1.5,
                  cursor: r.systemName ? 'pointer' : 'default',
                  transition: 'border-color 0.15s ease',
                  '&:hover': { borderColor: hexToRgba(rowAccent, 0.6) },
                }}
              >
                <Box sx={{ position: 'relative', flexShrink: 0, lineHeight: 0 }}>
                  <Avatar
                    variant="square"
                    src={`https://images.evetech.net/characters/${r.eveCharacterId}/portrait?size=64`}
                    sx={{ width: 40, height: 40, borderRadius: '3px', border: `1px solid ${hexToRgba(rowAccent, 0.55)}` }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -2,
                      bottom: -2,
                      width: 13,
                      height: 13,
                      borderRadius: '50%',
                      bgcolor: C.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Tooltip title={PRESENCE_LABEL(r)}>
                      <Box component="span" aria-label={PRESENCE_LABEL(r)} sx={{ display: 'flex' }}>
                        <Chevron color={PRESENCE_DOT(r.presence)} size={8} pulse={r.presence === 'online'} />
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.02rem', minWidth: { xs: 0, sm: 130 } }}>
                  {r.name}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: { xs: 0, sm: 200 } }}>
                  {r.systemName ? (
                    <>
                      <Typography sx={{ color: classColor(r.systemClass), fontFamily: FONT.mono, fontWeight: 700, fontSize: '0.92rem' }}>
                        {r.systemName}
                      </Typography>
                      {r.systemClass && !classIsRedundant && (
                        <StatusBadge label={r.systemClass} accent={classColor(r.systemClass)} size="md" />
                      )}
                    </>
                  ) : (
                    <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.88rem' }}>no location data</Typography>
                  )}
                </Stack>
                <Box sx={{ flex: 1 }} />
                {r.shipTypeName && (
                  <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.85rem' }}>{r.shipTypeName}</Typography>
                )}
                {isJSpace && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(isExpanded ? null : r.eveCharacterId);
                    }}
                    sx={{ color: C.textDim, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              {isJSpace && (
                <Collapse in={isExpanded}>
                  <RosterSystemSummary solarSystemId={r.solarSystemId!} onOpen={() => onOpenSystem(r.systemName!)} />
                </Collapse>
              )}
            </Box>
          );
        })}
      </Stack>
    </ConsolePanel>
  );
};

const RosterSystemSummary: React.FC<{ solarSystemId: number; onOpen: () => void }> = ({ solarSystemId, onOpen }) => {
  const { data: intel, isLoading } = useQuery({ queryKey: ['intel', 'system', solarSystemId], queryFn: () => fetchSystemIntel(solarSystemId) });

  return (
    <Box sx={{ pl: 5, pt: 1.25, pb: 0.75 }}>
      {isLoading ? (
        <Typography sx={{ color: C.textDim, fontSize: '0.85rem' }}>Loading&hellip;</Typography>
      ) : intel ? (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.85rem' }}>
            {intel.effect?.label ?? 'no effect'} &middot; statics:{' '}
            {intel.staticsCatalogued ? intel.statics.map((s) => s.static_type_code).join(', ') : 'uncatalogued'}
          </Typography>
          <IconButton size="small" onClick={onOpen} sx={{ color: C.cyan, p: 0.25 }}>
            <RoomIcon fontSize="inherit" />
          </IconButton>
        </Stack>
      ) : null}
    </Box>
  );
};

export default Dashboard;
